import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FiMail, FiUser, FiMessageSquare } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will respond shortly.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-primary-main mb-6" data-testid="contact-heading">
            Research Enquiries
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Contact us for institutional purchasing, bulk orders, or research collaboration enquiries.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="contact-name">
                  <FiUser className="inline w-4 h-4 mr-2" />
                  Full Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="contact-email">
                  <FiMail className="inline w-4 h-4 mr-2" />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="contact-message">
                  <FiMessageSquare className="inline w-4 h-4 mr-2" />
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="contact-submit-button"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-background-subtle border border-slate-200 rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-4">Research Institutions</h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                We work with research institutions, universities, and laboratories across Australia. 
                For bulk orders or institutional purchasing arrangements, please use the contact form 
                and include details about your institution and research requirements.
              </p>
            </div>

            <div className="bg-background-subtle border border-slate-200 rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-4">Quality Questions</h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                For questions about specific batches, COA verification, or quality standards, 
                please include the product SKU and batch number in your message.
              </p>
            </div>

            <div className="bg-background-subtle border border-slate-200 rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-primary-main mb-4">Important Notice</h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                We cannot provide advice on dosage, administration, or human/veterinary use. 
                All products are for research purposes only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
